// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Bill department routing content renderer.
 * Used by BillRendererDialog to display routed departments and assignees.
 */
import { type FC, useCallback, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Check, Copy, Users } from 'lucide-react'

import type { BillDepartmentRouting } from '../../models/types'

/** Format routing data as plain text for clipboard */
function formatAsPlainText(routing: BillDepartmentRouting): string {
  const lines: string[] = []

  if (routing.summary) {
    lines.push('Summary', routing.summary, '')
  }

  for (const route of routing.routedDepartments) {
    lines.push(route.department)
    lines.push(`Assignees: ${route.assignees.join(', ')}`)
    lines.push(`Rationale: ${route.rationale}`)
    if (route.citations?.length) {
      lines.push('Citations:')
      for (const citation of route.citations) {
        lines.push(`  "${citation}"`)
      }
    }
    lines.push('')
  }

  return lines.join('\n').trimEnd()
}

/** Content renderer for bill-department-routing component type */
export const BillDepartmentRoutingContent: FC<{ data: unknown }> = ({ data }) => {
  const routing = data as BillDepartmentRouting
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(() => {
    if (!routing) return
    void navigator.clipboard.writeText(formatAsPlainText(routing)).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [routing])

  if (!routing?.routedDepartments) return null

  return (
    <div className="space-y-6 py-4">
      {/* Copy button */}
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={handleCopy} className="gap-2">
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>

      {/* Summary */}
      {routing.summary && (
        <Card>
          <CardHeader>
            <CardTitle>Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {routing.summary}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Routed Departments */}
      {routing.routedDepartments.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">
            Departments ({routing.routedDepartments.length})
          </h3>
          {routing.routedDepartments.map((route, index) => (
            <Card key={index}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{route.department}</CardTitle>
                  <Badge variant="secondary" className="gap-1 flex-shrink-0 ml-2">
                    <Users className="h-3 w-3" />
                    {route.assignees.length}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Assignees */}
                <div className="flex flex-wrap gap-2">
                  {route.assignees.map((assignee) => (
                    <Badge key={assignee} variant="outline">
                      {assignee}
                    </Badge>
                  ))}
                </div>

                {/* Rationale */}
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {route.rationale}
                </p>

                {/* Citations */}
                {route.citations && route.citations.length > 0 && (
                  <div className="space-y-2 pt-2 border-t">
                    <p className="text-xs font-medium text-muted-foreground">Supporting Citations</p>
                    {route.citations.map((citation, citationIndex) => (
                      <blockquote key={citationIndex} className="text-xs border-l-2 pl-3 italic text-muted-foreground whitespace-pre-wrap">
                        {citation}
                      </blockquote>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
